import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useRef, useState } from 'react';
const ConnectorContext = /*#__PURE__*/ createContext({
    connector: null,
    connectorDocumentId: null,
    setConnector: ()=>{},
    whenConnectorReady: ()=>Promise.reject(new Error('OnlyOfficeConnectorProvider is not mounted'))
});
export function OnlyOfficeConnectorProvider({ children }) {
    const [connector, setConnectorState] = useState(null);
    const [connectorDocumentId, setConnectorDocumentId] = useState(null);
    // Live mirror so whenConnectorReady() closures and waiter-flushing see the
    // current value synchronously (React state updates are async).
    const currentRef = useRef({
        connector: null,
        documentId: null
    });
    const waitersRef = useRef([]);
    const setConnector = useCallback((c, documentId = null)=>{
        currentRef.current = {
            connector: c,
            documentId
        };
        setConnectorState(c);
        setConnectorDocumentId(documentId);
        if (c) {
            // Resolve waiters for this document. An untagged connector satisfies any
            // waiter (volume/studio case, where there is only ever one editor).
            const remaining = [];
            for (const w of waitersRef.current){
                if (!documentId || w.documentId === documentId) {
                    clearTimeout(w.timer);
                    w.resolve(c);
                } else {
                    remaining.push(w);
                }
            }
            waitersRef.current = remaining;
        } else {
            // Connector cleared (drawer closed / editor unmounted): fail any pending
            // waiter promptly instead of leaving it to time out.
            for (const w of waitersRef.current){
                clearTimeout(w.timer);
                w.reject(new Error('The document editor was closed'));
            }
            waitersRef.current = [];
        }
    }, []);
    const whenConnectorReady = useCallback((documentId, timeoutMs = 10000)=>{
        const cur = currentRef.current;
        if (cur.connector && (!cur.documentId || cur.documentId === documentId)) {
            return Promise.resolve(cur.connector);
        }
        return new Promise((resolve, reject)=>{
            const waiter = {
                documentId,
                resolve,
                reject,
                timer: setTimeout(()=>{
                    waitersRef.current = waitersRef.current.filter((w)=>w !== waiter);
                    reject(new Error('Timed out waiting for the document editor to open'));
                }, timeoutMs)
            };
            waitersRef.current.push(waiter);
        });
    }, []);
    return /*#__PURE__*/ _jsx(ConnectorContext.Provider, {
        value: {
            connector,
            connectorDocumentId,
            setConnector,
            whenConnectorReady
        },
        children: children
    });
}
export function useOnlyOfficeConnector() {
    return useContext(ConnectorContext);
}
